import { faCopy } from '@fortawesome/pro-light-svg-icons';
import { CustomLink, DateDistanceDisplay, Toast } from '@ygtechnologies/bygr-frontend-component-library';
import { FlatCard } from '@/components/FlatCard';
import { createDataTable } from '@/components/DataTable';
import PrimaryCard from '@/components/Statistics/PrimaryCard';
import React from 'react';
import { useParams } from 'react-router';
import { getResidentialUnit, setModuleForResidentialUnits } from '@/services/residentialUnit';
import { getSystemInfo } from '@/services/system';
import { BygrBuyer, BygrModule, InvitationStatus } from '@/types';
import { useAsyncState, useAsyncStateWithIsLoading } from '@/utils/hooks';
import { showModules } from '@/utils/util';
import theme from './ResidentialUnit.module.scss';
import { SlideToggle, Button } from '@ygtechnologies/rubus-ui';

interface ResidentialUnitProps {}

const BuyerTable = createDataTable<BygrBuyer>();

const ResidentialUnit: React.FC<ResidentialUnitProps> = () => {
    const { residentialUnitId } = useParams<{ residentialUnitId: string }>();
    const [systemInfo] = useAsyncState(() => getSystemInfo(), [], null);

    // const [showDialog, setShowDialog] = useState(false);
    const [data, , reloadData] = useAsyncStateWithIsLoading(
        () => getResidentialUnit(+residentialUnitId),
        [residentialUnitId],
        null
    );
    console.log(data);

    const hasModule = (module: BygrModule) => module === (data!.buyerModules & module);

    const toggleModule = async (module: BygrModule) => {
        if (hasModule(module)) {
            await setModuleForResidentialUnits([data!.id], data!.buyerModules & ~module);
        } else {
            await setModuleForResidentialUnits([data!.id], data!.buyerModules | module);
        }
        reloadData();
    };

    return (
        <div className={theme.container}>
            <PrimaryCard loading={!data}>
                {() => (
                    <>
                        <PrimaryCard.Row label={'Name'}>{data!.name}</PrimaryCard.Row>
                        <PrimaryCard.Row label={'Floor'}>{data!.floor}</PrimaryCard.Row>
                        <PrimaryCard.Row label={'Buyer module'}>{showModules(data!.buyerModules)}</PrimaryCard.Row>
                        <PrimaryCard.Row label={'Seller module'}>{showModules(data!.sellerModules)}</PrimaryCard.Row>
                    </>
                )}
            </PrimaryCard>
            <FlatCard contentClassName={theme.modules} loading={!data} title="Buyer Modules">
                {() => (
                    <>
                        <SlideToggle
                            onChange={() => toggleModule(BygrModule.ChangeManagement)}
                            checked={hasModule(BygrModule.ChangeManagement)}>
                            Change management
                        </SlideToggle>

                        <SlideToggle
                            checked={hasModule(BygrModule.Aftermarket)}
                            onChange={() => toggleModule(BygrModule.Aftermarket)}>
                            Aftermarket
                        </SlideToggle>
                    </>
                )}
            </FlatCard>
            <FlatCard colSpan={3} contentClassName={theme.buyers} loading={!data} title="Buyers">
                {() => (
                    <>
                        <BuyerTable hasPagination={false} data={data!.buyers}>
                            <BuyerTable.Column
                                field="firstName"
                                header="Name"
                                renderer={({ row }) =>
                                    row.applicationUserId ? (
                                        <CustomLink to={`/users/${row.applicationUserId}`}>
                                            {row.firstName} {row.lastName}
                                        </CustomLink>
                                    ) : (
                                        <>
                                            {row.firstName} {row.lastName}
                                        </>
                                    )
                                }
                            />
                            <BuyerTable.Column field="email" header="Email" />
                            <BuyerTable.Column field="phoneNumber" header="Phone Number" />
                            <BuyerTable.Column field="languageCode" header="Language" />
                            <BuyerTable.Column field="invitationStatus" header="Invitation status" />
                            <BuyerTable.Column
                                field="token"
                                header="Copy invite"
                                renderer={({ value, row }) => (
                                    <Button
                                        size="xs"
                                        disabled={row.invitationStatus !== InvitationStatus.Sent}
                                        title='Cannot copy invite link unless invitation status is "Sent"'
                                        icon={faCopy}
                                        onClick={async () => {
                                            const inviteUrl = `${systemInfo?.frontendUrl}/cm/invitation?token=${value}`;
                                            navigator.clipboard.writeText(inviteUrl);
                                            Toast.success('Copied invitation link to clipboard');
                                        }}
                                        variant="tertiary"
                                    />
                                )}
                            />

                            <BuyerTable.Column
                                field="termsAccepted"
                                header="Accepted terms"
                                renderer={({ value }) => (value ? 'Yes' : 'No')}
                            />
                            <BuyerTable.Column
                                field="lastVisit"
                                header="Last visit"
                                renderer={({ value }) => <DateDistanceDisplay value={value!} />}
                            />
                        </BuyerTable>
                    </>
                )}
            </FlatCard>
        </div>
    );
};

export default ResidentialUnit;
